import { useState, ViewTransition } from 'react';
import { Link, NavLink, Outlet, ScrollRestoration, useLocation } from 'react-router';

import { useApiContext } from '@/api/context';
import { useAllComponents, useAllQueues, useMe, usePrefetchIncidents } from '@/api/queries';
import { useIdentity } from '@/auth/AuthProvider';
import { roleLabel, usePerson } from '@/auth/roles';
import { Button } from '@/components/Button';
import { CountPill, HealthDot } from '@/components/badges';
import { Dialog } from '@/components/Dialog';
import { Field, Input } from '@/components/fields';
import { Moon, Sun, User } from '@/components/icons';
import { NavIndicator, TabLabel } from '@/components/NavIndicator';
import { SCOPE_LABEL, worstOf } from '@/domain/health';
import { SCOPES } from '@/domain/types';

import { usePreferences } from './preferences';
import { scopePath } from './scope';

export function Shell() {
  const { pathname } = useLocation();
  return (
    <div className="min-h-dvh flex flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-50 focus:rounded-sm focus:bg-surface focus:px-3 focus:py-2 focus:text-ink"
      >
        Skip to content
      </a>
      <TopBar />
      <ScopeStrip />
      {/* Moving between pages: the old one fades out and the new one rises in; the frame stays. */}
      <ViewTransition key={pageKey(pathname)} enter="page-in" exit="page-out" default="none">
        <main
          id="main"
          tabIndex={-1}
          className="flex-1 w-full max-w-[1440px] mx-auto px-4 sm:px-6 py-6 outline-none"
        >
          <Outlet />
        </main>
      </ViewTransition>
      <ScrollRestoration />
    </div>
  );
}

/** The part of the path that names a page frame: the overview, one scope, or one proposal. */
function pageKey(pathname: string): string {
  return pathname.split('/').filter(Boolean).slice(0, 2).join('/') || 'overview';
}

/* The time-zone switch is a preference, not a call to action: its chosen segment is filled in the bar's own line colour, not white. */
const TZ_ON = 'bg-topbar-line text-topbar-ink';
const TZ_OFF = 'text-topbar-muted hover:text-topbar-ink';

function TopBar() {
  const { mode } = useApiContext();
  const { timeZone, setTimeZone, resolvedTheme, setTheme, approver } = usePreferences();
  const [editing, setEditing] = useState(false);
  const identity = useIdentity();
  const person = usePerson();
  const me = useMe();

  const nameButton = (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="h-7 inline-flex items-center gap-1.5 rounded-sm px-2 text-meta text-topbar-muted transition-tone pressable hover:text-topbar-ink hover:bg-topbar-line max-w-[180px]"
      title="The name recorded on your decisions"
    >
      <User size={15} />
      <span className="truncate">{approver || 'Set your name'}</span>
    </button>
  );

  return (
    <header className="bg-topbar text-topbar-ink border-b border-topbar-line">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 h-[52px] flex items-center gap-4">
        <Link
          to="/"
          className="flex items-center gap-3 text-topbar-ink no-underline hover:no-underline min-w-0"
        >
          {/* Verisk's blue lockup, drawn white on the navy bar (the bar is navy in both themes). */}
          <img
            src="/verisk-logo.png"
            alt="Verisk"
            width={1059}
            height={301}
            className="h-6 w-auto shrink-0 brightness-0 invert"
          />
          <span aria-hidden="true" className="h-4 w-px bg-topbar-line" />
          <span className="text-body text-topbar-muted truncate">
            Synergy Studio Status Monitor
          </span>
        </Link>

        {mode === 'mock' ? (
          <span
            className="hidden sm:inline-flex h-5 items-center rounded-sm bg-status-impaired/20 px-1.5 text-meta font-medium text-status-impaired"
            title="No API URL configured: in-memory sample data. Reload to reset."
          >
            Sample data
          </span>
        ) : null}

        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <div
            role="group"
            aria-label="Time zone"
            className="flex rounded-sm border border-topbar-line overflow-hidden text-meta font-medium"
          >
            <button
              type="button"
              aria-pressed={timeZone === 'utc'}
              onClick={() => setTimeZone('utc')}
              className={`h-7 px-2.5 transition-tone ${timeZone === 'utc' ? TZ_ON : TZ_OFF}`}
            >
              UTC
            </button>
            <button
              type="button"
              aria-pressed={timeZone === 'local'}
              onClick={() => setTimeZone('local')}
              className={`h-7 px-2.5 transition-tone ${timeZone === 'local' ? TZ_ON : TZ_OFF}`}
            >
              Local
            </button>
          </div>

          <button
            type="button"
            onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
            aria-label={
              resolvedTheme === 'dark' ? 'Switch to the light theme' : 'Switch to the dark theme'
            }
            className="h-7 w-7 inline-flex items-center justify-center rounded-sm text-topbar-muted transition-tone pressable hover:text-topbar-ink hover:bg-topbar-line"
          >
            {resolvedTheme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          {identity.enabled ? (
            <>
              {person ? (
                <span
                  className="h-7 inline-flex items-center gap-1.5 px-2 text-meta text-topbar-muted max-w-[240px]"
                  title={person.email}
                >
                  <User size={15} />
                  <span className="truncate">{`${person.name || person.email} · ${roleLabel(person.role)}`}</span>
                </span>
              ) : me.data?.auth === 'off' ? (
                nameButton
              ) : null}
              <button
                type="button"
                onClick={identity.signOut}
                className="h-7 rounded-sm px-2 text-meta text-topbar-muted transition-tone pressable hover:text-topbar-ink hover:bg-topbar-line"
              >
                Sign out
              </button>
            </>
          ) : (
            nameButton
          )}
        </div>
      </div>
      <ApproverDialog open={editing} onClose={() => setEditing(false)} />
    </header>
  );
}

export function ApproverDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { approver, setApprover } = usePreferences();
  const [name, setName] = useState(approver);
  const [error, setError] = useState<string | undefined>();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Your name"
      description="Recorded on every decision you make."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="approver-form">
            Save name
          </Button>
        </>
      }
    >
      <form
        id="approver-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (!name.trim()) {
            setError('Enter your name.');
            return;
          }
          setApprover(name);
          setError(undefined);
          onClose();
        }}
      >
        <Field label="Name" error={error} required>
          {(props) => (
            <Input
              {...props}
              name="approver"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Priya Natarajan"
            />
          )}
        </Field>
      </form>
    </Dialog>
  );
}

function ScopeStrip() {
  const components = useAllComponents();
  const queues = useAllQueues();
  const prefetchIncidents = usePrefetchIncidents();
  const { pathname } = useLocation();

  return (
    <nav aria-label="Scopes" className="bg-surface border-b border-line">
      <div className="relative max-w-[1440px] mx-auto px-4 sm:px-6 flex items-stretch gap-1 overflow-x-auto overflow-y-hidden scrollbar-thin">
        <StripLink to="/" end label="Overview" />
        {SCOPES.map((scope, i) => {
          const view = components[i]?.data;
          const worst = view ? worstOf(view.components.map((c) => c.state.published_status)) : null;
          const pending = queues[i]?.data?.length ?? 0;
          return (
            <StripLink
              key={scope}
              to={scopePath(scope)}
              label={SCOPE_LABEL[scope]}
              onWarm={() => prefetchIncidents(scope)}
            >
              {worst ? (
                <HealthDot health={worst} />
              ) : (
                <span
                  className="inline-block size-2 rounded-full bg-status-none-soft"
                  aria-hidden="true"
                />
              )}
              <TabLabel>{SCOPE_LABEL[scope]}</TabLabel>
              <CountPill count={pending} tone="attention" />
            </StripLink>
          );
        })}
        <NavIndicator activeKey={pathname} />
      </div>
    </nav>
  );
}

function StripLink({
  to,
  end,
  label,
  onWarm,
  children,
}: {
  to: string;
  end?: boolean;
  label: string;
  /** Called on hover or focus, before the click, so the destination can be fetched early. */
  onWarm?: () => void;
  children?: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end ?? false}
      {...(children ? { 'aria-label': label } : {})}
      onPointerEnter={onWarm}
      onFocus={onWarm}
      className={({ isActive }) =>
        [
          // The first tab drops its left padding so its label sits on the content edge, under
          // the wordmark and over the page title; the focus ring is drawn inside the scroller.
          'inline-flex items-center gap-2 h-10 px-3 first:pl-0 text-lead whitespace-nowrap no-underline hover:no-underline transition-tone focus-visible:-outline-offset-2',
          isActive ? 'text-brand font-medium' : 'text-ink-2 hover:text-ink',
        ].join(' ')
      }
    >
      {children ?? <TabLabel>{label}</TabLabel>}
    </NavLink>
  );
}
